import { useEffect, useRef, useState } from 'react';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({ 
  children, 
  className = '', 
  delay = 0,
  direction = 'up' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Use a more reliable intersection observer configuration
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only animate once
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            
            // Use requestAnimationFrame for smoother animation start
            requestAnimationFrame(() => {
              if (delay > 0) {
                setTimeout(() => {
                  requestAnimationFrame(() => {
                    setIsVisible(true);
                  });
                }, delay);
              } else {
                setIsVisible(true);
              }
            });
            
            // Unobserve after triggering
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.05, // Lower threshold to trigger earlier
        rootMargin: '0px 0px 100px 0px', // Trigger 100px before element enters viewport for faster appearance
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [delay]);

  const getTransform = () => {
    if (!isVisible) {
      switch (direction) {
        case 'up':
          return 'translate3d(0, 30px, 0)';
        case 'down':
          return 'translate3d(0, -30px, 0)';
        case 'left':
          return 'translate3d(30px, 0, 0)';
        case 'right':
          return 'translate3d(-30px, 0, 0)';
        case 'fade':
          return 'translate3d(0, 0, 0)';
        default:
          return 'translate3d(0, 40px, 0)';
      }
    }
    return 'translate3d(0, 0, 0)';
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        willChange: isVisible ? 'auto' : 'transform, opacity',
        transition: isVisible 
          ? 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)'
          : 'none',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        perspective: 1000,
      }}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;

